// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { PowerFlowConductor } from '../../../../utils/powerFlowRouting';
import type { BreakerState } from '../../../../utils/powerFlowLive';
import {
  breakerFlowEntry,
  breakerMarkState,
  breakerPlacement,
  breakerTitle,
  type BreakerPlacement,
} from '../../../../utils/powerFlowBreakers';
import { BreakerGlyph } from './BreakerGlyph';
import {
  alarmLine,
  alarmsFor,
  withAlarmLines,
  type AlarmsByAsset,
} from '../../../../utils/powerFlowAlarms';
import type { BreakerAlarmTarget } from './BreakerAlarmPopover';

export { breakerMarkState };

export interface BreakerMarkProps {
  conductor: PowerFlowConductor;
  /** Breaker id → live state. Absent draws the box with unknown position. */
  breakerOf?: Map<string, BreakerState>;
  /** Short name for the hover title; defaults to the id. */
  labelOf?: (_id: string) => string;
  /** Asset id → open alarms; the breaker's own are appended to its title. */
  alarmsOf?: AlarmsByAsset;
  /** The ALARM mark was activated. Absent leaves the mark inert. */
  onAlarmClick?: (_target: BreakerAlarmTarget) => void;
  dimmed?: boolean;
  /** On the traced path: the glyph takes the trace hue with its conductor. */
  highlighted?: boolean;
  /** Where to draw it; defaults to `breakerPlacement(conductor)`. */
  at?: BreakerPlacement;
  /**
   * The conductor's own live paint classes (`conductorClasses`): the breaker
   * is drawn in exactly the colour and fade of the run it sits on, so the two
   * read as one drawing.
   */
  paint?: readonly string[];
  /** Load is passing through: the flow dash rides the arc. */
  flowing?: boolean;
}

/**
 * A circuit breaker, drawn as a series annotation on the conductor.
 *
 * The drawout breaker as the site's own engineering one-line draws it
 * (`BreakerGlyph`): chevron drawout contacts and the contact arc, on the
 * conductor's `anchor` — the drop out of the board — which `powerFlowRouting`
 * reserved for exactly this.
 *
 * Position is the contact arc's geometry, never a fill: **seated** on both
 * contacts closed, **swung** off the bottom contact open, seated but **dashed**
 * unknown, so no reading can pass
 * for open. Alarm is a separate cue — a warn triangle standing off the arc, the
 * shape `StatusMark` uses — so it never blurs the glyph.
 * On the traced path the whole glyph takes the trace hue with its conductor.
 * Text is the hover title only — 144 labels on a site would be noise; the
 * detail panel is where a breaker is named.
 *
 * ponytail: one mark per conductor even when two breakers were folded onto it
 * (a bypass has one at each end); split into a mark per end if the two ever
 * need distinct states.
 */
export const BreakerMark = ({
  conductor,
  breakerOf,
  labelOf,
  alarmsOf,
  onAlarmClick,
  dimmed = false,
  highlighted = false,
  at,
  paint = [],
  flowing = false,
}: BreakerMarkProps) => {
  const via = conductor.via ?? [];
  if (via.length === 0) return null;
  const { position, alarm } = breakerMarkState(via, breakerOf);
  const placement = at ?? breakerPlacement(conductor);
  const { x, y, horizontal = false } = placement;
  const classes = ['sld-breaker', `sld-breaker--${position}`];
  if (alarm) classes.push('sld-breaker--alarm');
  if (dimmed) classes.push('is-dimmed');
  if (highlighted) classes.push('is-highlighted');
  classes.push(...paint);
  const title = withAlarmLines(
    breakerTitle(via, breakerOf, labelOf),
    alarmsFor(alarmsOf, via).map(alarmLine)
  );
  const entry = flowing ? breakerFlowEntry(conductor.points, placement) : undefined;

  return (
    <g
      className={classes.join(' ')}
      transform={`translate(${x} ${y})`}
      data-sld-breaker={via.join(' ')}
      data-sld-breaker-orientation={horizontal ? 'horizontal' : 'vertical'}
      data-sld-breaker-position={position}
      data-sld-breaker-alarm={alarm ? 'true' : undefined}
    >
      <title>{title}</title>
      {/* Flow only through a breaker nothing says is open. */}
      <BreakerGlyph
        position={position}
        alarm={alarm}
        flowing={flowing && position !== 'open'}
        flowEntry={entry?.distance}
        flowReversed={entry?.reversed}
        horizontal={horizontal}
        onAlarmClick={onAlarmClick && (() => onAlarmClick({ ids: [...via], x, y, horizontal }))}
      />
    </g>
  );
};
